import React from 'react';
import styles from './FreightChargeDetails.styles';
import { IFreightChargeDetailsHandle, IFreightChargeDetailsProperties } from './FreightChargeDetails.properties';
import { withStyles } from '@material-ui/core';
import FormView from 'components/FormView';
import FreightChargeDetailsForm from './FreightChargeDetails.form';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { useAsyncClickHandler } from 'utils/hooks';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { Cancel as CancelIcon, CheckCircle } from '@markinson/uicomponents-v2/SvgIcons';
import { useFetchFreightDetails, useRetrieveProductReceiptContext, useRetrieveProductReceiptSummary, useUpdateFreightDetails } from 'api/productReceipts/productReceipts';
import { usePRContextSelector, usePRDispatch } from '../ProductReceipts.context';
import { ProductReceiptsActionTypes } from '../ProductReceipts.actions';

const FreightChargeDetails = (props: IFreightChargeDetailsProperties, ref: React.Ref<IFreightChargeDetailsHandle>) => {
    const { classes, formValues, dirty, formAsyncErrors, changeSelectedTab, destoryForm, changeFormFieldValue } = props;
    const [internalDirty, setInternalDirty] = React.useState<boolean>(false);
    const isDirty = internalDirty || dirty;
    const onCancelClicked = React.useRef(false);
    const searchProductReceiptId = Number(new URLSearchParams(location.search).get('ProductReceipt'));
    const productReceiptId = usePRContextSelector<'ProductReceipt'>((state) => state.ProductReceipt) || searchProductReceiptId;
    const { data: FreightDetails, isLoading: freightDetailsDataLoading, isFetching: isFreightDetailsDataFetching, refetch } = useFetchFreightDetails(productReceiptId);
    const { mutateAsync: updateFreightDetailsMutateAsync } = useUpdateFreightDetails();
    const { refetch: refetchSummary } = useRetrieveProductReceiptSummary(productReceiptId);
    const retrieveProductReceiptContextMutation = useRetrieveProductReceiptContext();
    const contextDispatch = usePRDispatch();

    const { setActionBar } = React.useContext(ActionBarContext);

    const ok = React.useCallback(
        async () => {
            if (formValues.UseSeperateSupplier) {
                if (!formValues.SupplierId) {
                    showSnackBar({ variant: 'warning', message: 'No supplier entered!' });

                    return null;
                }

                if (formAsyncErrors?.SupplierId) {
                    showSnackBar({ variant: 'warning', message: 'Please enter valid supplier' });

                    return null;
                }

                if (!formValues.Reference) {
                    showSnackBar({ variant: 'warning', message: 'No reference entered!' });

                    return null;
                }
            }
            const response = await updateFreightDetailsMutateAsync({
                ...formValues,
                ProductReceiptId: productReceiptId,
                SupplierId: formValues.UseSeperateSupplier ? formValues.SupplierId : FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.SupplierId,
                Reference: formValues.UseSeperateSupplier ? formValues.Reference : FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.Reference,
                Comment: formValues.UseSeperateSupplier ? formValues.Comment : FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.Comment,
            });
            if (response?.Status === true) {
                destoryForm();
                setInternalDirty(false);

                refetchSummary();

                const productReceiptContextResponse = await retrieveProductReceiptContextMutation.mutateAsync({ ProductReceiptId: productReceiptId });
                if (response?.Status) {
                    contextDispatch({
                        [ProductReceiptsActionTypes.setProductReceiptContext]: productReceiptContextResponse.ProductReceiptContext?.inlineObject,
                    });
                }

                changeSelectedTab('Lines');
            }

            return response;
        },
        [formValues]
    );

    const { onClick: onOk } = useAsyncClickHandler(ok, !freightDetailsDataLoading);

    const onCancel = React.useCallback(
        (): void => {
            changeSelectedTab('Lines');
        },
        [changeSelectedTab, onCancelClicked]
    );

    const rightIcons = React.useMemo(
        () => [
            {
                label: 'Cancel',
                Icon: CancelIcon,
                iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                action: 'FreightChargeDetailsCancel',
                callBack: onCancel
            },
            {
                label: 'OK',
                Icon: CheckCircle,
                action: 'FreightChargeDetailsOk',
                callBack: onOk
            },
        ],
        []
    );

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons: [],
                rightIcons
            });
        },
        []
    );

    React.useImperativeHandle(
        ref,
        () => ({
            isChangesSavedOrDiscarded(): boolean {
                if (isDirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !isDirty;
            },
            FreightChargeDetailsOk: onOk,
            FreightChargeDetailsCancel: onCancel,
            refetchFreightChargeDetails: refetch,
        }),
        [isDirty, onOk, onCancel, formValues]
    );

    const handleUseSeperateOnChange = () => {
        changeFormFieldValue('SupplierId', '');
        changeFormFieldValue('Reference', '');
        changeFormFieldValue('Comment', '');

        return null;
    };

    return (
        <div className={classes.formContainer}>
            <FormView
                isLoading={freightDetailsDataLoading || isFreightDetailsDataFetching}
                formName={'ProductReceiptFreightDetails'}
                schema={FreightChargeDetailsForm}
                includeTabsHeight={false}
                initialValues={{
                    ...FreightDetails?.ProductReceiptFreightDetails?.inlineObject,
                    SupplierId: FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.UseSeperateSupplier ? FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.SupplierId : '',
                    Reference: FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.UseSeperateSupplier ? FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.Reference : '',
                    Comment: FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.UseSeperateSupplier ? FreightDetails?.ProductReceiptFreightDetails?.inlineObject?.Comment : ''
                }}
                valuesSchema={FreightDetails?.ProductReceiptFreightDetails?.schema}
                operationMode={'EDIT'}
                fieldExtensions={{
                    UseSeperateSupplier: {
                        onChange: handleUseSeperateOnChange
                    },
                    SupplierId: {
                        disabled: !formValues?.UseSeperateSupplier,
                    },
                    Reference: {
                        disabled: !formValues?.UseSeperateSupplier,
                    },
                    Comment: {
                        disabled: !formValues?.UseSeperateSupplier,
                    },
                }}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(FreightChargeDetails)));
